import React, { useState } from "react";
import { toast } from "react-toastify";
import {
  assignSecurityIncidentInvestigator,
  addSecurityIncidentInvestigationNote,
  completeSecurityIncidentInvestigation,
  refreshSecurityIncidentSLA,
} from "../../../service/securityService";
import type { SecurityIncident, SecurityInvestigationNote } from "../../../types/security_types";

interface SecurityInvestigationPanelProps {
  incident: SecurityIncident;
  orgUsers: Array<{
    _id?: string;
    id?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
  }>;
  onUpdated: () => void;
}

const SecurityInvestigationPanel: React.FC<SecurityInvestigationPanelProps> = ({
  incident,
  orgUsers,
  onUpdated,
}) => {
  const id = incident.id || incident._id;
  const [investigatorId, setInvestigatorId] = useState(
    incident.assignedInvestigator || ""
  );
  const [note, setNote] = useState("");
  const [report, setReport] = useState(incident.investigationReport || "");
  const [busy, setBusy] = useState(false);

  if (!id) return null;

  const assign = async () => {
    if (!investigatorId) {
      toast.error("Select an investigator");
      return;
    }
    const u = orgUsers.find((x) => (x._id || x.id) === investigatorId);
    const name =
      [u?.firstName, u?.lastName].filter(Boolean).join(" ").trim() ||
      u?.name ||
      investigatorId;
    setBusy(true);
    try {
      await assignSecurityIncidentInvestigator(id, {
        investigatorId,
        investigatorName: name,
      });
      toast.success("Investigator assigned");
      onUpdated();
    } catch (e) {
      console.error(e);
      toast.error("Could not assign investigator");
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) {
      toast.error("Enter a note");
      return;
    }
    setBusy(true);
    try {
      await addSecurityIncidentInvestigationNote(id, {
        note: note.trim(),
        noteType: "General",
      });
      toast.success("Note added");
      setNote("");
      onUpdated();
    } catch (e) {
      console.error(e);
      toast.error("Could not add note");
    } finally {
      setBusy(false);
    }
  };

  const complete = async () => {
    setBusy(true);
    try {
      await completeSecurityIncidentInvestigation(id, {
        investigationReport: report.trim(),
      });
      toast.success("Investigation marked complete");
      onUpdated();
    } catch (e) {
      console.error(e);
      toast.error("Could not complete investigation");
    } finally {
      setBusy(false);
    }
  };

  const sla = async () => {
    setBusy(true);
    try {
      await refreshSecurityIncidentSLA(id);
      toast.success("SLA refreshed");
      onUpdated();
    } catch (e) {
      console.error(e);
      toast.error("SLA update failed");
    } finally {
      setBusy(false);
    }
  };

  const notes: SecurityInvestigationNote[] = incident.investigationNotes || [];
  const invStatus = incident.investigationStatus || "Not Started";
  const completed = invStatus === "Completed";

  return (
    <div className="border-t border-gray-200 mt-6 pt-4 px-6 pb-6 space-y-5">
      <h3 className="text-lg font-semibold text-gray-800">Post-incident investigation</h3>
      <div className="flex flex-wrap gap-3 text-sm">
        <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-800 font-medium">
          Status: {invStatus}
        </span>
        {incident.slaStatus && (
          <span className="px-2 py-1 rounded-full bg-amber-50 text-amber-900 font-medium">
            SLA: {incident.slaStatus}
            {incident.slaTargetDate
              ? ` · target ${new Date(incident.slaTargetDate).toLocaleString()}`
              : ""}
          </span>
        )}
        <button
          type="button"
          disabled={busy || completed}
          onClick={() => sla()}
          className="text-[#0fa9a6] font-semibold hover:underline disabled:opacity-40"
        >
          Recalculate SLA
        </button>
      </div>

      {!completed && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Assign investigator</label>
            <div className="flex gap-2 flex-wrap">
              <select
                value={investigatorId}
                onChange={(e) => setInvestigatorId(e.target.value)}
                className="flex-1 min-w-[200px] border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Select user</option>
                {orgUsers.map((u) => {
                  const uid = u._id || u.id;
                  if (!uid) return null;
                  const label =
                    [u.firstName, u.lastName].filter(Boolean).join(" ").trim() ||
                    u.name ||
                    uid;
                  return (
                    <option key={uid} value={uid}>
                      {label}
                    </option>
                  );
                })}
              </select>
              <button
                type="button"
                disabled={busy}
                onClick={() => assign()}
                className="px-4 py-2 rounded-full bg-[#0fa9a6] text-white text-sm font-semibold hover:bg-teal-600 disabled:opacity-50"
              >
                Assign
              </button>
            </div>
            {incident.assignedInvestigatorName && (
              <p className="text-xs text-gray-500">
                Current: {incident.assignedInvestigatorName}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Add investigation note</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder="Interview summary, evidence review, follow-up…"
            />
            <button
              type="button"
              disabled={busy}
              onClick={() => addNote()}
              className="px-4 py-2 rounded-full bg-gray-800 text-white text-sm font-semibold hover:bg-gray-900 disabled:opacity-50"
            >
              Add note
            </button>
          </div>
        </div>
      )}

      {notes.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Notes</h4>
          <ul className="space-y-2 text-sm text-gray-600 max-h-40 overflow-y-auto">
            {notes.map((n, i) => (
              <li key={i} className="border border-gray-100 rounded-lg p-2 bg-gray-50">
                <span className="text-xs text-gray-400">
                  {n.addedAt ? new Date(n.addedAt).toLocaleString() : ""}
                  {n.addedByName ? ` · ${n.addedByName}` : ""}
                </span>
                <p className="mt-1 whitespace-pre-wrap">{n.note}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!completed && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Investigation report (on close)</label>
          <textarea
            value={report}
            onChange={(e) => setReport(e.target.value)}
            rows={4}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            placeholder="Final findings, root cause summary, recommendations…"
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => complete()}
            className="px-4 py-2 rounded-full bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800 disabled:opacity-50"
          >
            Complete investigation
          </button>
        </div>
      )}

      {completed && incident.investigationReport && (
        <div className="text-sm">
          <h4 className="font-semibold text-gray-700 mb-1">Final report</h4>
          <p className="text-gray-600 whitespace-pre-wrap">{incident.investigationReport}</p>
        </div>
      )}
    </div>
  );
};

export default SecurityInvestigationPanel;
